// De staat van de matchwizard: antwoorden kiezen, stappen doorlopen, onthouden tussen bezoeken.
//
// Alles client-side (epic 12, besluit A1). De antwoorden zijn ~twintig kleine waarden; die passen in
// `localStorage`, net als de favorieten al doen. Daarmee bewaart de server niets van een anonieme
// bezoeker — geen tabel, geen bewaartermijn, geen opruimtaak, geen verwijderknop die iets moet
// waarmaken. Het sterkste privacyantwoord is dat er niets te verwijderen valt.
//
// Pure functies met de antwoorden als argument, zodat dit zonder DOM te testen is; `DierMatchMatch`
// houdt er alleen React-state omheen.

import {
  STAPPEN,
  stapCompleet,
  vragenVoor,
  zichtbareStappen,
  type Antwoorden,
  type StapId,
  type Vraag,
} from "./vragen";

/** Draagt een versie voor als de vorm wijzigt — oude opgeslagen antwoorden vallen dan stil weg. */
const SLEUTEL = "diermatch.match.v1";

export const EERSTE_STAP: StapId = STAPPEN[0].id;

/** De opgeslagen stand: de antwoorden plus waar de bezoeker gebleven was. */
export interface Bewaard {
  antwoorden: Antwoorden;
  stap: StapId;
  /**
   * Of de bezoeker bij de matchlijst stond.
   *
   * <p>Hoort bij de stand, niet bij het scherm: wie een dier aanklikt en daarna terugkomt moet
   * zijn tien matches terugzien en niet het introscherm. Zonder dit moest je de hele vragenlijst
   * opnieuw langs om de andere negen te bekijken.
   */
  lijst: boolean;
}

const LEEG: Bewaard = { antwoorden: {}, stap: EERSTE_STAP, lijst: false };

function isStap(waarde: unknown): waarde is StapId {
  return typeof waarde === "string" && STAPPEN.some((s) => s.id === waarde);
}

/** Leest de opgeslagen stand; alles wat niet klopt wordt genegeerd in plaats van gerepareerd. */
export function lees(): Bewaard {
  try {
    const ruw = globalThis.localStorage?.getItem(SLEUTEL);
    if (!ruw) {
      return LEEG;
    }
    const gelezen = JSON.parse(ruw) as unknown;
    if (!gelezen || typeof gelezen !== "object") {
      return LEEG;
    }
    const bron = gelezen as { antwoorden?: unknown; stap?: unknown };
    const antwoorden: Antwoorden = {};
    if (bron.antwoorden && typeof bron.antwoorden === "object") {
      for (const [sleutel, waarde] of Object.entries(bron.antwoorden as Record<string, unknown>)) {
        if (Array.isArray(waarde)) {
          antwoorden[sleutel] = waarde.filter((w): w is string => typeof w === "string");
        }
      }
    }
    return {
      antwoorden,
      stap: isStap(bron.stap) ? bron.stap : EERSTE_STAP,
      lijst: (gelezen as { lijst?: unknown }).lijst === true,
    };
  } catch {
    return LEEG;
  }
}

export function bewaar(stand: Bewaard): void {
  try {
    globalThis.localStorage?.setItem(SLEUTEL, JSON.stringify(stand));
  } catch {
    // Vol of geblokkeerd: dan onthoudt de browser het niet. De wizard werkt gewoon door — dit is
    // een geheugensteun, geen opslag waar iets van afhangt.
  }
}

export function wis(): void {
  try {
    globalThis.localStorage?.removeItem(SLEUTEL);
  } catch {
    // Zie hierboven.
  }
}

// ── antwoorden wijzigen ─────────────────────────────────────────────────────────────────────────

/**
 * Het antwoord op één vraag na een klik op `waarde`.
 *
 * <p>Drie vormen, één functie, omdat het scherm anders per vraagvorm zou moeten weten hoe een
 * antwoord verandert — en dat is precies de kennis die hier hoort.
 *
 * <ul>
 *   <li><b>enkel</b> — klikken kiest; nog eens klikken op dezelfde keuze laat hem los, zodat een
 *       niet-verplichte vraag ook weer onbeantwoord kan worden.</li>
 *   <li><b>meervoud</b> — klikken zet aan of uit. Een exclusieve optie ("Nee", "Maakt niet uit")
 *       wist de rest, en elke andere keuze wist de exclusieve.</li>
 *   <li><b>tekst</b> — de waarde vervangt wat er stond; leeg betekent geen antwoord.</li>
 * </ul>
 */
export function kies(vraag: Vraag, huidig: string[], waarde: string): string[] {
  if (vraag.vorm === "tekst") {
    return waarde.trim() ? [waarde] : [];
  }
  if (vraag.vorm === "enkel") {
    return huidig.includes(waarde) ? [] : [waarde];
  }
  const optie = vraag.opties?.find((o) => o.waarde === waarde);
  if (huidig.includes(waarde)) {
    return huidig.filter((w) => w !== waarde);
  }
  if (optie?.exclusief) {
    return [waarde];
  }
  const exclusieven = new Set(
    (vraag.opties ?? []).filter((o) => o.exclusief).map((o) => o.waarde),
  );
  return [...huidig.filter((w) => !exclusieven.has(w)), waarde];
}

/** De antwoorden ná een klik — de vorm die het scherm gebruikt. */
export function metKeuze(antwoorden: Antwoorden, vraag: Vraag, waarde: string): Antwoorden {
  const volgende = kies(vraag, antwoorden[vraag.sleutel] ?? [], waarde);
  const uit = { ...antwoorden };
  if (volgende.length === 0) {
    delete uit[vraag.sleutel];
  } else {
    uit[vraag.sleutel] = volgende;
  }
  return uit;
}

/**
 * Een voorstel uit de vrije wens overnemen (story 12-8).
 *
 * <p>Zetten, niet omschakelen. {@link metKeuze} hoort bij een klik en haalt een keuze die al aan
 * staat er weer af — dat is goed gedrag voor een vinger, en fout gedrag voor "neem deze drie
 * voorstellen over", want dan zou een voorstel dat toevallig al klopt zijn eigen antwoord wissen.
 */
export function metVoorstel(antwoorden: Antwoorden, vraag: Vraag, waarde: string): Antwoorden {
  if ((antwoorden[vraag.sleutel] ?? []).includes(waarde)) {
    return antwoorden;
  }
  return metKeuze(antwoorden, vraag, waarde);
}

// ── stappen ─────────────────────────────────────────────────────────────────────────────────────

/**
 * Waar deze stap staat in de route die déze bezoeker loopt.
 *
 * <p>Niet in {@link STAPPEN} maar in {@link zichtbareStappen}: wie een kat zoekt heeft geen stap
 * "Wonen", en dan is "stap 3 van 5" een nummer dat nergens op slaat. Een stap die ondertussen
 * verdwenen is valt terug op de eerste — dat kan gebeuren als een antwoord eerder in de wizard de
 * route verandert terwijl je er al voorbij was.
 */
export function stapIndex(stap: StapId, antwoorden: Antwoorden): number {
  const i = zichtbareStappen(antwoorden).indexOf(stap);
  return i < 0 ? 0 : i;
}

export function volgendeStap(stap: StapId, antwoorden: Antwoorden): StapId {
  const route = zichtbareStappen(antwoorden);
  return route[Math.min(stapIndex(stap, antwoorden) + 1, route.length - 1)];
}

export function vorigeStap(stap: StapId, antwoorden: Antwoorden): StapId {
  const route = zichtbareStappen(antwoorden);
  return route[Math.max(stapIndex(stap, antwoorden) - 1, 0)];
}

/** Voortgang over de vraagstappen van deze route; het resultaat telt niet mee als vraag. */
export function voortgang(stap: StapId, antwoorden: Antwoorden): { nummer: number; totaal: number } {
  const vraagstappen = zichtbareStappen(antwoorden).filter((s) => s !== "resultaat");
  const totaal = Math.max(vraagstappen.length, 1);
  return { nummer: Math.min(stapIndex(stap, antwoorden) + 1, totaal), totaal };
}

/**
 * Of de bezoeker rechtstreeks naar deze stap mag springen via de stappenbalk.
 *
 * <p>Terug mag altijd. Vooruit alleen voorbij stappen die af zijn, zodat de balk geen weg aanbiedt
 * die meteen weer terugduwt — en de verplichte vragen niet over te slaan zijn.
 */
export function magNaar(doel: StapId, huidig: StapId, antwoorden: Antwoorden): boolean {
  const route = zichtbareStappen(antwoorden);
  const naar = route.indexOf(doel);
  // Een stap die niet op de route van deze bezoeker ligt, is geen bestemming. Zonder deze regel
  // viel een weggevallen stap terug op index 0 en leek hij altijd bereikbaar.
  if (naar < 0) {
    return false;
  }
  if (naar <= stapIndex(huidig, antwoorden)) {
    return true;
  }
  return route.slice(0, naar).every((s) => s === "resultaat" || stapCompleet(s, antwoorden));
}

/** Hoeveel van de zichtbare vragen van een stap beantwoord zijn — voor het vinkje in de balk. */
export function stapStand(stap: StapId, antwoorden: Antwoorden): { gedaan: number; totaal: number } {
  const vragen = vragenVoor(stap, antwoorden);
  const gedaan = vragen.filter((v) => (antwoorden[v.sleutel] ?? []).length > 0).length;
  return { gedaan, totaal: vragen.length };
}
